import { useState, type ReactNode } from 'react';
import { Column, DataGrid } from './DataGrid';

const LETTERS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

interface Props<T extends Record<string, unknown>> {
  title: string;
  columns: Column<T>[];
  useData: (grouping?: string, search?: string) => {
    data?: T[];
    isFetching: boolean;
  };
  rowAction?: (row: T) => ReactNode;
  headerExtra?: ReactNode;
}

/** "Client starts with" grouping + name search over a server-paged grid. */
export function GroupedGrid<T extends Record<string, unknown>>({
  title,
  columns,
  useData,
  rowAction,
  headerExtra,
}: Props<T>) {
  const [grouping, setGrouping] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { data, isFetching } = useData(grouping || undefined, search || undefined);

  return (
    // Fill the viewport height so the grid gets a bounded, internally-scrolling area
    // (its horizontal scrollbar stays on screen instead of at the bottom of 500 rows).
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-shrink-0 flex-wrap items-end gap-3">
        <div>
          <label className="label">Client starts with</label>
          <select
            className="input w-40"
            value={grouping}
            onChange={(e) => setGrouping(e.target.value)}
          >
            <option value="">All</option>
            {LETTERS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="label">Search (Lookup Client Name)</label>
          <div className="flex gap-2">
            <input
              className="input"
              placeholder="Client name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
            />
            <button className="btn-secondary" onClick={() => setSearch(searchInput)}>
              Search
            </button>
          </div>
        </div>
        {headerExtra}
      </div>

      <h2 className="flex-shrink-0 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      <div className="min-h-0 flex-1">
        <DataGrid columns={columns} rows={data ?? []} loading={isFetching} rowAction={rowAction} />
      </div>
      <p className="flex-shrink-0 text-xs text-muted-foreground">Showing up to 500 most-recent matches. Narrow with the filters above.</p>
    </div>
  );
}
